import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Tag } from "lucide-react";
import { useApp } from "@/state/AppContext";
import { usePageTitle } from "@/lib/usePageTitle";
import { api, rows } from "@/lib/api";
import { localized } from "@/theme/tenantTheme";
import { OfferBadge, offerEndsOn } from "@/components/OfferBadge";
import { shortDate } from "@/lib/format";
import { ListError } from "@/components/operator/ui/ListError";

/**
 * Every live offer in the city — the web half of a screen the app has had all along.
 *
 * Pure reuse: the same `GET /offers` the city home's offers strip already draws from, and the
 * same `OfferBadge` every business card renders. Nothing new on the backend, and the three i18n
 * strings it needs (`offers.cityTitle`, `citySubtitle`, `cityEmpty`) were already written and
 * used nowhere — they were left behind for this page.
 */
export default function Offers() {
  const { t, lang, tenant } = useApp();
  usePageTitle(t("nav.offers")); // RF-21: per-page tab title
  const [offers, setOffers] = useState(null);
  const [failed, setFailed] = useState(false);
  // Bumped by Retry: re-runs the load without a page refresh (R11 NEW-R10-4).
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!tenant) return;
    setOffers(null);
    setFailed(false);
    api
      .get("/offers", { params: { limit: 100 } })
      .then((r) => setOffers(rows(r.data)))
      .catch(() => setFailed(true));
  }, [tenant, attempt]);

  return (
    <div className="mx-auto max-w-7xl px-5 py-12 sm:px-8">
      <h1 className="font-heading text-4xl font-bold tracking-tight sm:text-5xl">
        {t("offers.cityTitle", { city: localized(lang, tenant, "name") })}
      </h1>
      <p className="mt-2 max-w-2xl text-base text-muted-foreground">{t("offers.citySubtitle")}</p>

      {failed ? (
        <ListError
          className="mt-8"
          message={t("common.connectionTrouble")}
          onRetry={() => setAttempt((n) => n + 1)}
          testId="offers-error"
        />
      ) : offers === null ? (
        <p className="mt-8 text-center text-muted-foreground">{t("common.loading")}</p>
      ) : offers.length === 0 ? (
        <p
          className="mt-6 rounded-2xl border border-dashed border-border p-10 text-center text-muted-foreground"
          data-testid="offers-empty"
        >
          {t("offers.cityEmpty")}
        </p>
      ) : (
        <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="offers-grid">
          {offers.map((offer) => (
            <OfferCard key={offer.id} offer={offer} lang={lang} t={t} />
          ))}
        </div>
      )}
    </div>
  );
}

function OfferCard({ offer, lang, t }) {
  const ends = offerEndsOn(offer);
  // B4: the card opens the offer's own detail page (which links on to the shop).
  const href = `/offers/${offer.id}`;
  const body = (
    <article
      className="flex h-full flex-col gap-3 rounded-2xl border border-border bg-card p-5 transition-transform duration-200 hover:-translate-y-1 hover:shadow-lg"
      data-testid={`offer-card-${offer.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <h2 className="font-heading text-lg font-semibold">{localized(lang, offer, "title")}</h2>
        <OfferBadge offer={offer} />
      </div>
      {localized(lang, offer, "description") && (
        <p className="text-sm text-muted-foreground">{localized(lang, offer, "description")}</p>
      )}
      <p className="mt-auto flex items-center gap-1.5 text-sm text-muted-foreground">
        <Tag size={14} />
        {[offer.business_name, ends && t("offers.endsOn", { date: shortDate(ends, lang) })]
          .filter(Boolean)
          .join(" · ")}
      </p>
    </article>
  );
  return href ? (
    <Link to={href} className="block h-full">
      {body}
    </Link>
  ) : (
    body
  );
}
